import type { ExcelNotesSheetLayout } from '@mss_creator/shared';
import { EXCEL_FONT_FAMILY, EXCEL_FONT_SIZE_PT } from '@mss_creator/shared';
import {
  BORDER_WIDTH_PX,
  CHROME_CLASS,
  GUTTER_WIDTH_PX,
  LETTER_ROW_HEIGHT_PX,
  columnLetters,
} from './excel-preview-chrome';

interface ExportExcelNotesSheetProps {
  layout: ExcelNotesSheetLayout;
}

/**
 * The « Notes » sheet of the Excel preview (issue #19): the shared
 * `ExcelNotesSheetLayout` - the very description the backend's writer consumes
 * - drawn as a spreadsheet, like the matrix sheet beside it: column letters,
 * row numbers, gridlines, the frozen header row.
 */
export function ExportExcelNotesSheet({ layout }: ExportExcelNotesSheetProps) {
  const tableWidthPx =
    GUTTER_WIDTH_PX + layout.columns.reduce((total, column) => total + column.widthPx, 0);
  const edge = `${BORDER_WIDTH_PX[layout.border.style]}px solid ${layout.border.hex}`;
  const gridlines = { borderRight: edge, borderBottom: edge };

  return (
    <table
      aria-label="Aperçu de la feuille Notes"
      className="border-separate border-spacing-0"
      style={{
        tableLayout: 'fixed',
        width: tableWidthPx,
        fontFamily: `"${EXCEL_FONT_FAMILY}", sans-serif`,
        fontSize: `${EXCEL_FONT_SIZE_PT}pt`,
      }}
    >
      <colgroup>
        <col style={{ width: GUTTER_WIDTH_PX }} />
        {layout.columns.map((column) => (
          <col key={column.label} style={{ width: column.widthPx }} />
        ))}
      </colgroup>
      <thead>
        <tr style={{ height: LETTER_ROW_HEIGHT_PX }}>
          <th
            aria-hidden="true"
            className={CHROME_CLASS}
            style={{ position: 'sticky', top: 0, left: 0, zIndex: 3 }}
          />
          {layout.columns.map((column, index) => (
            <th
              key={column.label}
              scope="col"
              className={CHROME_CLASS}
              style={{ position: 'sticky', top: 0, zIndex: 2 }}
            >
              {columnLetters(index)}
            </th>
          ))}
        </tr>
        <tr style={{ height: `${layout.headerHeightPt}pt` }}>
          <th
            scope="row"
            className={CHROME_CLASS}
            style={{ position: 'sticky', top: LETTER_ROW_HEIGHT_PX, left: 0, zIndex: 3 }}
          >
            1
          </th>
          {layout.columns.map((column) => (
            <th
              key={column.label}
              scope="col"
              className="px-1 text-left align-top"
              style={{
                ...gridlines,
                position: 'sticky',
                top: LETTER_ROW_HEIGHT_PX,
                zIndex: 2,
                backgroundColor: layout.headerFillHex,
                fontWeight: 700,
              }}
            >
              {column.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {layout.rows.map((row, rowIndex) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: sheet rows are identified by their position
          <tr key={rowIndex} style={{ height: `${row.heightPt}pt` }}>
            <th
              scope="row"
              className={CHROME_CLASS}
              style={{ position: 'sticky', left: 0, zIndex: 1 }}
            >
              {rowIndex + 2}
            </th>
            {row.cells.map((value, cellIndex) => (
              <td
                // biome-ignore lint/suspicious/noArrayIndexKey: a row's cells are identified by their column position
                key={cellIndex}
                className="bg-white px-1 text-left align-top"
                style={{ ...gridlines, fontWeight: cellIndex === 0 ? 700 : 400 }}
              >
                {/* A fixed-height Excel row clips its content - so does the preview. */}
                <div
                  className="whitespace-pre-wrap"
                  style={{ maxHeight: `${row.heightPt}pt`, overflow: 'hidden' }}
                >
                  {value}
                </div>
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
